import {
  EmailType,
  FormEntity,
  LANGUAGE,
  Menu,
  ModifierProperty,
  ModifierType,
  ModuleEntity,
  QCCompareType,
  QCInputType,
  SixteenSMLQueueEnum,
  Subscriptions,
} from '@/enums';
import {
  BasePdfTemplate,
  PdfTemplate,
} from '@/pages/authenPages/Organization/PDFTemplateConfig/interface';

export interface DBConfig {
  subscriptionPlan?: Subscriptions;
  organization?: {
    name: string;
    supportedLanguages?: LANGUAGE[];
    pathLabel?: string; // 'https://(oga).lilac.amili.asia/translation/' // When the lilac application run they will find in /translation/us.json & cn.json
    customInputQCRules?: IInputQCRules[];
    pdfOutputTemplate?: 'TEMPLATE1' | 'TEMPLATE2' | 'TEMPLATE3';
    customRuleExternalId?: IRuleExternalId;
    projectConfiguration?: {
      pdfCustomizationTemplates?: PdfTemplate[] | PDFCustomizationTemplates[];
      specificProjectConfiguration?: SpecificProjectConfiguration[];
      basePDFSettings?: BasePdfTemplate;
      // pdfCustomizationTemplatesTest?: PdfTemplate[];
    };
    forms?: Record<FormEntity, ICustomForm[]>;
    emails?: Record<EmailType, EmailCustomization>;
    customRoles?: ICustomRole[];
    defaultSixteenSQueue?: SixteenSMLQueueEnum;
    customDropdownOptions?: {
      [entity in ModuleEntity]?: { disableOptions: string[] };
    };
    customModuleConfiguration?: ModuleConfigurationEntity;
  };
}

export type ComponentType = 'form' | 'tab' | 'button' | 'input';

type ModifierPropertyUnion = keyof typeof ModifierProperty;
type ModifierTypeForComponent<T extends ComponentType> = T extends 'tab'
  ? Exclude<ModifierPropertyUnion, 'DISABLED'>
  : ModifierPropertyUnion;

export type PropertyConfigEntity<K extends ComponentType> = {
  key: string;
  disabledKeys?: string[];
  removeKeys?: string[];
  insertKeys?: string[];
  modifier?: ModifierTypeForComponent<K>;
};

export type ComponentConfigEntity = {
  [Component in ComponentType]?: Component extends 'form'
    ? Record<string, PropertyConfigEntity<'form'>[]>
    : PropertyConfigEntity<Component>[];
};

export type ModuleConfigurationEntity = {
  [ModuleName in ModuleEntity]?: ComponentConfigEntity;
};

interface ICustomRole {
  name: string; // 'Operation Plus'
  enableMenu: Menu[];
  scopes: ICustomRolesScope[];
}

interface EmailCustomization {
  enable: boolean;
  customEmailTemplateNameInSES?: string;
  customSenderName?: string;
}

interface IClientLogo {
  key: string;
  bucket: string;
  source: string;
}

interface IDoctorLetter {
  source: string;
  text: string[];
}

interface ICustomization {
  'Patient name'?: boolean;
  'More Health Insights'?: boolean;
  'Patient name placeholder'?: string;
  'Probiotics Recommendation'?: boolean;
  'Disease Prediction'?: boolean;
  'Client logo'?: IClientLogo;
  'AMILI logo'?: IClientLogo;
  'Title page'?: IClientLogo | boolean;
  'Doctor signature'?: IClientLogo | boolean;
  'Doctor Letter'?: IDoctorLetter | boolean;
  'Doctor name'?: string[] | boolean;
  'filename prefix'?: string;
  template?: string;
  report_name_prefix?: string;
  mlInputPrefix?: string;
}

export interface PDFCustomizationTemplates {
  templateName: string;
  customization: ICustomization;
  pdfVersion?: string;
  foodSets?: string[];
}

interface SpecificProjectConfiguration {
  name: string;
  configurations: {
    dada2?: boolean;
    attributesExtra?: string[];
  };
}

interface IInputQCRules {
  input: QCInputType;
  compareType: QCCompareType;
  value: string | number;
}

interface IRuleExternalId {
  min: number;
  max: number;
  prefix?: string;
  regexValidation?: string;
}

export interface ICustomForm {
  field?: string;
  modifier?: ModifierType;
  customOptionDisableKeys?: string[];
  customOptionRemoveKeys?: string[];
  customOptions?: string[];
}

interface ICustomRolesScope {
  scope_id: number;
  name: string;
  entity: string;
}

export const amiliDBConfig: DBConfig = {
  subscriptionPlan: Subscriptions.SUBSCRIPTION_A,
  organization: {
    name: 'amili-lilac',
    customRoles: [],
    projectConfiguration: {
      pdfCustomizationTemplates: [
        {
          templateName: 'Alexandra Hospital',
          customization: {
            'Patient name': false,
            'More Health Insights': false,
            'Patient name placeholder': 'patient',
            'Probiotics Recommendation': false,
          },
        },
        {
          templateName: 'Remove Patient Name',
          customization: {
            'Patient name': false,
          },
        },
        {
          templateName: 'Remove Disease Risk',
          customization: {
            'Disease Prediction': false,
          },
        },
        {
          templateName: 'Qcare Individualized Medicine Company Limited',
          customization: {
            'Patient name': false,
            'More Health Insights': false,
            'Patient name placeholder': 'patient',
          },
        },
        {
          templateName: 'SamataSoul',
          customization: {
            'Client logo': {
              key: 'SamataSoul/samatasoul.png',
              bucket: 'amili-bme-generatepdf',
              source: 's3',
            },
          },
        },
        {
          templateName: 'SuperDNA',
          customization: {
            'AMILI logo': {
              key: 'SuperDNA/transparent_background.png',
              bucket: 'amili-bme-generatepdf',
              source: 's3',
            },
            'Title page': false,
            'Doctor name': false,
            'Patient name': false,
            'Doctor Letter': false,
            'Doctor signature': false,
            'Probiotics Recommendation': false,
          },
        },
        {
          templateName: 'Uni Lab',
          customization: {
            'AMILI logo': {
              key: 'BioBalance/transparent_background.png',
              bucket: 'amili-bme-generatepdf',
              source: 's3',
            },
            'Client logo': {
              key: 'BioBalance/BioBalance.png',
              bucket: 'amili-bme-generatepdf',
              source: 's3',
            },
            'Patient name': false,
            'More Health Insights': false,
            'Patient name placeholder': 'patient',
            'Probiotics Recommendation': false,
          },
        },
        {
          templateName: 'Vinmec International Clinic',
          customization: {
            'Patient name': false,
            'Patient name placeholder': 'patient',
            'Probiotics Recommendation': false,
          },
        },
      ],
      specificProjectConfiguration: [
        {
          name: 'Hannah',
          configurations: {
            dada2: true,
          },
        },
        {
          name: 'Thu Thao',
          configurations: {
            dada2: true,
          },
        },
      ],
    },
    customDropdownOptions: {
      [ModuleEntity.ANALYSIS]: {
        disableOptions: ['delete'],
      },
    },
  },
};
